import { Component, OnInit, ViewChild } from '@angular/core';
import { BaseWithBundleComponent } from '@app/shared/components/base/base-with-bundle.component';
import { ComponentServiceBundle } from '@app/core/services/base/component-service-bundle.service';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { ActionListItem } from '@app/shared/components/action-list/action-list.component';
import { HasId } from '@app/shared/models/base';
import { UserCoreComponent } from '@app/shared/components/user/user-core/user-core.component';
import { UserAddressComponent } from '@app/shared/components/user/user-address/user-address.component';
import { PersonalService } from '@app/core/services/personal.service';
import { AuthenticationMethod, ClientUser } from '@app/shared/models/client-user';
import { DrPasswordComponent } from '@app/shared/components/dr-password/dr-password.component';
import { SmpError } from '@app/shared/models/smp-error';
import { HttpErrorResponse } from '@angular/common/http';
import { FormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ClientUserUriBuilderParams, UserService } from '@app/core/services/user.service';
import { Client } from '@app/shared/models/client';
import { User2FATotpComponent } from '@app/shared/components/user/user-2fa/user-2fa-totp.component';
import { User2FASmsComponent } from '@app/shared/components/user/user-2fa/user-2fa-sms.component';

export enum MyAccountItems {
    ADDRESS = 'ADDRESS',
    LOGIN = 'LOGIN',
    TWOFA = '2FA'
}

@Component({
    templateUrl: './my-account.component.html',
    styleUrls: ['./my-account.component.scss']
})
export class MyAccountComponent extends BaseWithBundleComponent
                                implements OnInit {

    public items: (ActionListItem & HasId)[];
    public selectedItem: ActionListItem & HasId;
    public meMyselfAndI: ClientUser;
    public password: string;
    public showCurrentPassword: boolean;
    public showAuthenticationMethods: boolean;
    
    public availableAuthenticationMethods: ({displayName: string} & HasId)[];

    public currentPasswordForm: UntypedFormGroup;

    @ViewChild('userCoreComponent')
    public userCoreComponent: UserCoreComponent;

    @ViewChild('userAddressComponent')
    public userAddressComponent: UserAddressComponent;

    @ViewChild('drPasswordComponent')
    public drPasswordComponent: DrPasswordComponent;
    
    @ViewChild('user2FASmsComponent')
    public user2FASmsComponent: User2FASmsComponent;

    @ViewChild('user2FATotpComponent')
    public user2FATotpComponent: User2FATotpComponent;

    get MyAccountItems() {
        return MyAccountItems;
    }

    get client(): Client {
        return this.personalService.currentUser.client;
    }

    get passwordCurrent(): any {
        return this.currentPasswordForm.get('passwordCurrent');
    }

    constructor(protected componentServiceBundle: ComponentServiceBundle,
                protected personalService: PersonalService,
                protected userService: UserService) {
        super(componentServiceBundle);
        this._idPrefix = 'personal.my-account';
    }

    public ngOnInit() {
        super.ngOnInit();

        this.currentPasswordForm = new UntypedFormGroup({
            passwordCurrent: new UntypedFormControl('', [Validators.required])
        })

        this.items = [
            { id: MyAccountItems.ADDRESS, actionListDisplayName: marker('personal.my_account.address')},
            { id: MyAccountItems.LOGIN, actionListDisplayName: marker('personal.my_account.login')},
            { id: MyAccountItems.TWOFA, actionListDisplayName: marker('personal.my_account.twoFA')}
        ];

        this.availableAuthenticationMethods = [
            { id: AuthenticationMethod.SMS, displayName: marker('personal.my_account.2fa.SMS') },
            { id: AuthenticationMethod.TOTP, displayName: marker('personal.my_account.2fa.TOTP') },
        ];

        this.showCurrentPassword = false;

        this.initMeMyselfAndI();
        this.selectItem(this.items[0]);
    }

    public ngOnDestroy(): void {
        super.ngOnDestroy();
    }

    public initMeMyselfAndI(): void {
        this.personalService.getMyUserData()
                            .subscribe((result) => {
            this.meMyselfAndI = result;
            this.userAddressComponent && this.userAddressComponent.ngOnInit();
            this.userCoreComponent && this.userCoreComponent.ngOnInit();
        });
    };

    public selectItem(item: ActionListItem & HasId): void {
        this.selectedItem = item;
    }

    public cancel(): void {
        this.initMeMyselfAndI();
        this.resetPasswordFields();
    }

    public save() {
        if (this.valid()) {
            this.formToModel();
            this.meMyselfAndI.address.copyPostalCodes();
            this.personalService.saveMyUserData(this.meMyselfAndI)
                .subscribe((response) => {
                    this.showSimpleConfirmSnackbar(marker('personal.my_account.message.user_data_saved'));
                }, (err) => {
                    if ((<SmpError>err).additional_code === 'validation.mandatory.field.missing.email') {
                        this.showSimpleErrorSnackbar(marker('user_management.error.email.required'));
                    } else if (err instanceof HttpErrorResponse && (<SmpError>err.error).additional_code ===  'validation.duplicate.email') {
                        this.userCoreComponent.email.setErrors({exists: true});
                        this.showSimpleErrorSnackbar(marker('user_management.error.email.required'));
                    } else if (err.status === 422) {
                        this.personalService.userService.promptForTan()
                                                        .then((tan: string) => {
                            this.meMyselfAndI.emailchange_tan = tan;
                            this.save();
                        }).catch(() => {
                            this.showSimpleErrorSnackbar(marker('user_management.dialog.tan.not_provided'));
                        });
                    } else {
                        this.showSimpleErrorSnackbar();
                    }

                    this.handleError(err);
                });
        }
    }

    public changePassword(): void {
        this.personalService.updateMyPassword(this.passwordCurrent.value, this.password)
                            .subscribe((response) => {
            this.resetPasswordFields();
            this.showSimpleConfirmSnackbar(marker('personal.my_account.message.new_password_saved'));
        }, (err: any) => {
            if (err.error && err.error.message === 'clients.business.registration.validation.password_invalid') {
                this.passwordCurrent.setErrors( { password_invalid: true });
                this.showSimpleErrorSnackbar(marker('login.recover_password.error.current_password_incorrect'));
            } 
            if (err.error && err.error.message === 'clients.business.registration.validation.old_password_equals_new') {
                this.showSimpleErrorSnackbar(marker('login.recover_password.error.old_password_equals_new'));
            } else {
                this.showSimpleErrorSnackbar();
            }
            this.handleError(err);
        });
    }

    public activateTwoFA(): void {
        this.showAuthenticationMethods = true;
    }

    public deactivateTwoFA(): void {
        this.showSimpleConfirmDialog(
            marker('personal.my_account.2fa.dialog.deactivate.title'),
            marker('personal.my_account.2fa.dialog.deactivate.text'), '400px'
        ).subscribe(result => {
            if (result) {
                this.userService.switchTwoFAMethod(this.generateUriBuilderParams(), AuthenticationMethod.STANDARD).subscribe(_ => {
                    this.meMyselfAndI.authentication_method = null;
                });
                this.showAuthenticationMethods = false;
            }
        });
    }

    public switchTwoFAMethod(method: AuthenticationMethod): void {
        
        let updateUser = () => {
            this.userService.switchTwoFAMethod(this.generateUriBuilderParams(), method).subscribe(_ => {
                this.meMyselfAndI.authentication_method = method;

                if (method === AuthenticationMethod.TOTP) {
                    this.user2FATotpComponent.setup();
                }
            });
        };

        if (!this.meMyselfAndI.isVerified) {
            if (!this.meMyselfAndI.authentication_method || this.meMyselfAndI.authentication_method !== method) {
                updateUser();
            }
        } else if (this.meMyselfAndI.authentication_method !== method) {
            this.showSimpleConfirmDialog(
                marker('personal.my_account.2fa.dialog.change_method.title'),
                marker('personal.my_account.2fa.dialog.change_method.text')
            ).subscribe(result => {
                if (result) {
                    updateUser();
                }
            });
        }
    }

    private resetPasswordFields(): void {
        this.passwordCurrent.reset();
        this.drPasswordComponent.ngOnInit();
    }

    // VALIDATION **************************************************************************************************

    private valid(): boolean {
        return this.userCoreComponent.valid() &&
               this.userAddressComponent.valid()
    }

    public formToModel(): void {
        this.meMyselfAndI.user_code = this.userAddressComponent.user_code.value;
        this.meMyselfAndI.email = this.userCoreComponent.email.value;
    }

    protected generateUriBuilderParams(): ClientUserUriBuilderParams {
        return new ClientUserUriBuilderParams(this.personalService.currentUser.client, this.personalService.currentUser.user.id)
    }
}
